import { $createCodeNode } from '@lexical/code';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import classNames from 'classnames';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  $createParagraphNode,
  $createTextNode,
  type ParagraphNode,
  type LexicalNode,
} from 'lexical';
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  List as ListIcon,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Code2,
  Undo2,
  Redo2,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SHOW_LINK_EDITOR_COMMAND } from './FloatingLinkEditorPlugin';
import { BlockType, ViewMode } from './MarkdownEditor.types';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

export type ToolbarProps = {
  viewMode: ViewMode;
  onChangeView: (mode: ViewMode) => void;
  disabled?: boolean;
};

export default function Toolbar({ viewMode, onChangeView, disabled = false }: ToolbarProps) {
  const { t } = useTranslation('knowledge_base');
  const [editor] = useLexicalComposerContext();

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>(BlockType.PARAGRAPH);
  const [canInsertLink, setCanInsertLink] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      setCanInsertLink(false);
      return;
    }

    const selectedText = selection.getTextContent();
    const hasSelection = !selection.isCollapsed() && selectedText.trim().length > 0;
    setCanInsertLink(hasSelection);

    setIsBold(selection.hasFormat('bold'));
    setIsItalic(selection.hasFormat('italic'));

    const anchorNode = selection.anchor.getNode();
    const element =
      anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
    const type = element.getType();

    if (type === 'paragraph') setBlockType(BlockType.PARAGRAPH);
    else if (type === 'quote') setBlockType(BlockType.QUOTE);
    else if (type === 'code') setBlockType(BlockType.CODE);
    else if (type === 'list') {
      // @ts-expect-error Lexical typing gap for custom method
      const listType = element.getListType?.() || 'bullet';
      setBlockType(listType === 'number' ? BlockType.OL : BlockType.UL);
    } else if (type === 'heading') {
      // @ts-expect-error Lexical typing gap for custom method
      const tag = element.getTag?.() || 'h1';
      setBlockType(tag === 'h2' ? BlockType.H2 : BlockType.H1);
    } else {
      setBlockType(BlockType.PARAGRAPH);
    }
  }, []);

  useEffect(() => {
    if (viewMode !== ViewMode.RICH_TEXT) return;
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => updateToolbar());
    });
  }, [editor, updateToolbar, viewMode]);

  const applyBlock = useCallback(
    (type: BlockType) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const shouldRevert =
          (type === BlockType.H1 && blockType === BlockType.H1) ||
          (type === BlockType.H2 && blockType === BlockType.H2) ||
          (type === BlockType.QUOTE && blockType === BlockType.QUOTE) ||
          (type === BlockType.CODE && blockType === BlockType.CODE) ||
          type === BlockType.PARAGRAPH;

        if (shouldRevert) {
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
          if (element.getType?.() === 'code') {
            const codeElem: any = element;
            const content = codeElem.getTextContent?.() ?? '';
            const lines = content.split('\n');
            const paragraphs = lines.map((line: string) => {
              const p = $createParagraphNode();
              const text = $createTextNode(line);
              if (p.append) p.append(text);
              return p;
            });
            paragraphs.reduce((prev: ParagraphNode | null, p: ParagraphNode) => {
              if (prev) (prev as any).insertAfter?.(p);
              else (codeElem as any).insertAfter?.(p);
              return p;
            }, null);
            codeElem.remove?.();
            paragraphs[0]?.select?.();
          } else {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        } else if (type === BlockType.H1) {
          $setBlocksType(selection, () => $createHeadingNode('h1'));
        } else if (type === BlockType.H2) {
          $setBlocksType(selection, () => $createHeadingNode('h2'));
        } else if (type === BlockType.QUOTE) {
          $setBlocksType(selection, () => $createQuoteNode());
        } else if (type === BlockType.CODE) {
          const nodes = selection.getNodes();
          const topLevels: LexicalNode[] = [];
          const seen = new Set<string>();

          nodes.forEach((node) => {
            const topLevel = node.getTopLevelElementOrThrow();
            const key = topLevel.getKey();
            if (seen.has(key)) return;
            seen.add(key);
            topLevels.push(topLevel);
          });

          if (topLevels.length === 0) {
            const topLevel = selection.anchor.getNode().getTopLevelElementOrThrow();
            topLevels.push(topLevel);
          }

          const alreadyCode = topLevels.every((node) => node.getType?.() === 'code');
          if (alreadyCode) return;

          const codeNode = $createCodeNode('tsx');
          const text = topLevels.map((node) => node.getTextContent()).join('\n');
          if (text.length > 0) {
            codeNode.append($createTextNode(text));
          }

          const firstTop = topLevels[0];
          firstTop.insertBefore(codeNode);
          topLevels.forEach((node) => node.remove());
          codeNode.selectEnd();
        }
      });
    },
    [blockType, editor],
  );

  const toggleBulletList = () => {
    if (blockType === BlockType.UL) editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    else editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  };

  const toggleOrderedList = () => {
    if (blockType === BlockType.OL) editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    else editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  };

  const toggleLink = () => {
    if (!canInsertLink) return;
    editor.focus();
    setTimeout(() => editor.dispatchCommand(SHOW_LINK_EDITOR_COMMAND, undefined), 0);
  };

  return (
    <div className="flex items-center gap-1 border-b border-border bg-accent px-2 py-1">
      <div className="flex items-center gap-0.5 py-1">
        <Button
          size="xs"
          variant={viewMode === ViewMode.RICH_TEXT ? 'primary_dark_light' : 'ghost'}
          onClick={() => onChangeView(ViewMode.RICH_TEXT)}
        >
          {t('article.markdown_toolbar.rich_text')}
        </Button>
        <Button
          size="xs"
          variant={viewMode === ViewMode.PLAIN_TEXT ? 'primary_dark_light' : 'ghost'}
          onClick={() => onChangeView(ViewMode.PLAIN_TEXT)}
        >
          {t('article.markdown_toolbar.plain_text')}
        </Button>
      </div>
      {viewMode === ViewMode.RICH_TEXT && (
        <TooltipProvider>
          <div className={classNames('flex items-center', disabled && 'pointer-events-none')}>
            <span className="mx-1 h-5 w-px bg-border" />
            <IconButton
              size="sm"
              variant="ghost"
              onClick={() => {
                editor.dispatchCommand(UNDO_COMMAND, undefined);
              }}
              aria-label="Undo"
            >
              <Undo2 />
            </IconButton>
            <IconButton
              size="sm"
              variant="ghost"
              onClick={() => {
                editor.dispatchCommand(REDO_COMMAND, undefined);
              }}
              aria-label="Redo"
            >
              <Redo2 />
            </IconButton>
            <span className="mx-1 h-5 w-px bg-border" />
            <Button
              size="sm"
              variant={blockType === BlockType.H1 ? 'primary_dark_light' : 'ghost'}
              onClick={() => applyBlock(BlockType.H1)}
            >
              <Heading1 />
            </Button>
            <Button
              size="sm"
              variant={blockType === BlockType.H2 ? 'primary_dark_light' : 'ghost'}
              onClick={() => applyBlock(BlockType.H2)}
            >
              <Heading2 />
            </Button>
            <IconButton
              size="sm"
              variant={isBold ? 'primary_dark_light' : 'ghost'}
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
              }}
              aria-label="Bold"
            >
              <Bold />
            </IconButton>
            <IconButton
              size="sm"
              variant={isItalic ? 'primary_dark_light' : 'ghost'}
              onClick={() => {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
              }}
              aria-label="Italic"
            >
              <Italic />
            </IconButton>
            <IconButton
              size="sm"
              variant={blockType === BlockType.CODE ? 'primary_dark_light' : 'ghost'}
              onClick={() => applyBlock(BlockType.CODE)}
              aria-label="Code block"
            >
              <Code2 />
            </IconButton>
            <span className="mx-1 h-5 w-px bg-border" />
            <IconButton
              size="sm"
              variant={blockType === BlockType.UL ? 'primary_dark_light' : 'ghost'}
              onClick={toggleBulletList}
              aria-label="Bullet list"
            >
              <ListIcon />
            </IconButton>
            <IconButton
              size="sm"
              variant={blockType === BlockType.OL ? 'primary_dark_light' : 'ghost'}
              onClick={toggleOrderedList}
              aria-label="Numbered list"
            >
              <ListOrdered />
            </IconButton>
            <IconButton
              size="sm"
              variant={blockType === BlockType.QUOTE ? 'primary_dark_light' : 'ghost'}
              onClick={() => applyBlock(BlockType.QUOTE)}
              aria-label="Quote"
            >
              <Quote />
            </IconButton>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={toggleLink}
                    aria-label="Insert link"
                    disabled={!canInsertLink}
                  >
                    <LinkIcon />
                  </IconButton>
                </span>
              </TooltipTrigger>
              {!canInsertLink && (
                <TooltipContent side="bottom">
                  {t('article.markdown_toolbar.link_tooltip')}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}
